// PromptV2 — single shared prompt input used by both DataExplorerViewV2
// and ComposerViewV2. Hosts that need an "above the input" surface
// (the composer's sketch / draft) pass `topSlot`; hosts that don't (data
// explorer) leave it empty and PromptV2 collapses to just the input row.
//
// Mental model: the prompt IS the sketch + the textarea. Both live
// inside the same pill — the user can describe their dashboard as
// text, as a sketch, or both. There's no separate "draft above prompt"
// surface; the sketch is part of the prompt.
//
// Layout (all inside one bordered pill):
//   ┌────────────────────────────────────────────────────────[⌃]──┐
//   │  topSlot (sketch / chip) — optional                          │
//   ├──────────────────────────────────────────────────────────────┤
//   │                              arraste/cole imagem (watermark) │
//   │  [✦] [⌚] [text input — autosize]                       [➤]  │
//   └──────────────────────────────────────────────────────────────┘
//
// Behaviour notes:
//   - The chevron at the top-right of `topSlot` is rendered by PromptV2
//     when `topSlotCollapsible` is true. Icon-on-hover pattern: hidden
//     until the user moves over the slot.
//   - When `topSlot` is provided AND expanded the textarea placeholder
//     is hidden (saves vertical space, matches the design).
//   - Suspend / Resume / Cancel live in RunProgressPanelV2 — the prompt
//     stays minimal during a run (just the "aguardando…" hint).
//   - The watermark "Arraste qualquer imagem para cá OU copie e cole"
//     sits inside the pill, top-right of the input row. Subtle at rest,
//     brighter on hover — future wiring will let users click it to
//     open a file picker.

import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTheme, alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CloseIcon from '@mui/icons-material/Close';
import Modal from '@mui/material/Modal';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import CircularProgress from '@mui/material/CircularProgress';
import Popper from '@mui/material/Popper';
import Paper from '@mui/material/Paper';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { type Dayjs } from 'dayjs';
import DataObjectIcon from '@mui/icons-material/DataObject';
import { useAiChatServices, useAiChatSessionStore } from '../AiChat/AiChatContext';
import { ClientMessage } from '../../models/ClientMessage';
import { ConnectionState } from '../../models/connectionState';
import { AgentGatewayWSAsync } from '../../services/servers/AgentGatewayWSAsync';
import { SparkleIcon, SendIcon, ChevronIcon, PauseIcon, StopIcon } from './icons';
import ModelSelector from '../AiChat/ChatPane/ModelSelector';
import SessionDataDialog from '../AiChat/ChatPane/SessionDataDialog';
import { LastPromptsV2 } from './LastPromptsV2';
import Stack from '@mui/material/Stack';

/**
 * Autocomplete item returned by `onSlashQuery` / `onAtQuery`. The
 * `value` is spliced verbatim into the textarea, replacing the entire
 * trigger token (the `/` or `@` plus whatever the user had typed after
 * it). Hosts can return `@username`, `/summarize`, `@[Name](id123)` —
 * whatever serialization fits their downstream parsing.
 */
export type PromptTriggerItem = {
    id: string;
    label: string;
    value: string;
    hint?: string;
};

interface PromptV2Props {
    forceMessage?: string | null;
    onHeightChange?: (newHeight: number) => void;

    /**
     * Optional uncontrolled-style seeder. When the value changes
     * PromptV2 replaces its internal draft with the new string
     * (without auto-sending — that's `forceMessage`'s job). Used by
     * suggestion clicks in the v2 composer's empty state.
     */
    seedDraft?: string | null;

    /** Show the prompt-history popover trigger inside the pill. Default true. */
    showLastPrompts?: boolean;

    showConfigOptions?: boolean;

    /**
     * Placeholder text for the textarea. Defaults to the data-explorer
     * phrasing ("Pergunte sobre seus dados…"); hosts in other domains
     * (e.g. a booking flow) pass their own. Ignored while an expanded
     * `topSlot` is shown — that surface owns the visual hierarchy and the
     * placeholder is suppressed regardless.
     */
    placeholder?: string;

    /**
     * Optional node rendered at the start of the input row, immediately
     * before the textarea (after the options / history controls). A slot
     * for a leading adornment — e.g. a shell-style `›` prompt glyph — that
     * tracks the input. Defaults to nothing.
     */
    inputPrefix?: React.ReactNode;

    /**
     * Optional surface rendered at the top of the pill. The composer
     * uses this to host its sketch (chip when collapsed / no sketch,
     * full toolbar+pad when expanded). The data explorer omits it.
     */
    topSlot?: React.ReactNode;
    /** When true, PromptV2 overlays a chevron at the top-right of `topSlot`. */
    topSlotCollapsible?: boolean;
    /** Controlled expanded state for `topSlot` (default true). */
    topSlotExpanded?: boolean;
    onTopSlotExpandedChange?: (next: boolean) => void;

    /**
     * Override the textarea's minimum height. Defaults to a compact
     * single-row look. The composer bumps this on the splash screen
     * (no sketch yet) so the prompt reads as the dominant surface.
     */
    inputMinHeight?: number;

    /**
     * Optional async hook that runs *before* the message hits the
     * gateway. The composer uses this to ensure a server-side
     * dashboard draft exists (so the agent's subsequent partials
     * carry the right `originalSessionId` for autosave).
     */
    onBeforeSend?: (text: string) => void | Promise<void>;

    /**
     * Full submit override. When provided, replaces the gateway send
     * entirely — the prompt does not call `agentGatewayService.sendMessage`
     * and `onBeforeSend` is not invoked. Used by the home empty-state to
     * route the typed text into a fresh /data-explorer session instead
     * of sending it on whatever provider happens to be mounted around
     * the splash. Connection / turn-open gating still applies (the send
     * button stays disabled while a turn is in flight); the empty-text
     * gate honors `allowEmptyDraft` as usual.
     */
    onSubmit?: (text: string) => void | Promise<void>;

    /**
     * Pass-through HTML attributes spread onto the send button. Used
     * by the dashboard composer to attach `data-env-id` /
     * `className="llm-env-target …"` so the LLM-driven spotlight tour
     * can address the V2 send button (the V1 `dashboard-pad-compose`
     * identifier). Spread BEFORE the component's own handlers/styles
     * so behavioural props (`onClick`, `disabled`, `aria-label`,
     * `sx`) always win.
     *
     * `color` is omitted because MUI's IconButton has a typed `color`
     * union that conflicts with HTML's free-string color attribute —
     * styling stays the responsibility of the component's own `sx`.
     */
    sendButtonProps?: Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'>;

    /**
     * Allow submitting with an empty textarea. Defaults to `false`,
     * which is the right behaviour for the data-explorer (the user's
     * intent IS the text). The dashboard composer flips this on when
     * the sketch has uncommitted edits — in that case the sketch
     * itself is the message content, and forcing the user to type
     * something would be busywork.
     */
    allowEmptyDraft?: boolean;

    /**
     * Optional node rendered inline in the action row, right before
     * the send button (only while no turn is open). The dashboard
     * composer uses this to surface a "+1 card · 1 filtro" chip when
     * the sketch has queued changes, so the user can see what's
     * about to be sent without us writing into their textarea.
     */
    submitAdornment?: React.ReactNode;

    /**
     * Async fetcher invoked while the user is typing a `/`-prefixed
     * token at the start of a word (i.e. preceded by whitespace or
     * line start — guards against false hits inside URLs). Called
     * with the query (everything after the `/` up to the caret) and
     * expected to return autocomplete items. Returning an empty array
     * suppresses the popover. Calls are debounced (~120ms) and stale
     * responses are discarded, so the host can implement this as a
     * naive `await fetch(...)` without worrying about ordering.
     */
    onSlashQuery?: (query: string) => Promise<PromptTriggerItem[]>;

    /**
     * Sibling of `onSlashQuery` for the `@` trigger — typically used
     * for mentions. Kept as a separate prop (rather than one
     * trigger-aware callback) so hosts that only wire one of the two
     * don't have to branch internally.
     */
    onAtQuery?: (query: string) => Promise<PromptTriggerItem[]>;

    /**
     * When set, `/command` tokens inside the prompt render as a chip
     * (soft tinted background + 1px inset border + matching text)
     * using this color as the dominant hue. Implemented via a
     * transparent-text textarea + a sibling mirror that paints the
     * styled tokens behind it, so the native caret / selection / IME
     * stay intact. Pass `theme.palette.primary.main` (or any palette
     * entry) for theme-coherent rendering.
     *
     * `slashColor` and `atColor` are independent: setting one
     * enables the overlay; the unset trigger renders as plain text.
     * Omitting both keeps the textarea in its non-highlighted state.
     */
    slashColor?: string;

    /** Sibling of `slashColor` for `@`-led tokens. */
    atColor?: string;

    /** Sibling of `slashColor` for `#`-led tokens (rendered after the
     *  user picks a date — `#YYYY-MM-DD` by default). */
    hashColor?: string;

    /**
     * When true, typing `#` at a word boundary opens an inline date
     * picker (anchored to the caret) instead of an autocomplete list.
     * Picking a date splices `#${formattedDate}` into the textarea,
     * replacing the trigger token. Defaults off — `#` stays as plain
     * text unless this is set.
     */
    enableHashDatePicker?: boolean;

    /**
     * dayjs format string used when inserting the picked date.
     * Defaults to `'YYYY-MM-DD'` (ISO — unambiguous and trivially
     * parseable on the receiving end). Hosts that want a localized
     * display can pass e.g. `'DD/MM/YYYY'`.
     */
    hashDateFormat?: string;

    borderRadius?: number;
}

const TEXTAREA_MIN_HEIGHT = 70;
const TEXTAREA_MAX_HEIGHT = 300;

// Highlight tokenizer — splits the draft into a flat list of runs so
// the overlay can paint chip styling around `/cmd` / `@user` tokens
// while keeping the surrounding text plain. The boundary rule mirrors
// `detectTrigger` exactly (trigger char at line start or after
// whitespace), so a `/` inside `http://` or an `@` inside an email
// stays plain — wrapping rendering in sync with how the autocomplete
// is wired prevents "chip appears but no popover" inconsistencies.
type HighlightToken = { type: 'plain' | 'slash' | 'at' | 'hash'; text: string };
const TRIGGER_CHARS: ReadonlySet<string> = new Set(['/', '@', '#']);
const triggerTypeFor = (ch: string): 'slash' | 'at' | 'hash' =>
    ch === '/' ? 'slash' : ch === '@' ? 'at' : 'hash';
// A token boundary is ASCII whitespace only — NOT the non-breaking space (U+00A0).
// Hosts join a multi-word `@mention` (e.g. "@São Paulo") with NBSPs so the whole
// name stays a single chip here; a regular space still separates distinct tokens.
const HIGHLIGHT_WS = /[ \t\r\n\f\v]/;
const tokenizeForHighlight = (text: string): HighlightToken[] => {
    if (!text) return [];
    const tokens: HighlightToken[] = [];
    let i = 0;
    while (i < text.length) {
        const ch = text[i];
        const atBoundary = i === 0 || HIGHLIGHT_WS.test(text[i - 1]);
        if (atBoundary && TRIGGER_CHARS.has(ch)) {
            let end = i + 1;
            while (end < text.length && !HIGHLIGHT_WS.test(text[end])) end += 1;
            tokens.push({ type: triggerTypeFor(ch), text: text.slice(i, end) });
            i = end;
        } else {
            let end = i + 1;
            while (end < text.length) {
                const c = text[end];
                const prevIsWs = HIGHLIGHT_WS.test(text[end - 1]);
                if (TRIGGER_CHARS.has(c) && prevIsWs) break;
                end += 1;
            }
            tokens.push({ type: 'plain', text: text.slice(i, end) });
            i = end;
        }
    }
    return tokens;
};

export const PromptV2 = forwardRef<HTMLDivElement, PromptV2Props>(({
    forceMessage,
    onHeightChange,
    seedDraft,
    showLastPrompts = true,
    placeholder: placeholderText,
    inputPrefix,
    topSlot,
    borderRadius = TEXTAREA_MIN_HEIGHT,
    topSlotCollapsible = false,
    topSlotExpanded = true,
    showConfigOptions = true,
    onTopSlotExpandedChange,
    inputMinHeight,
    onBeforeSend,
    onSubmit,
    sendButtonProps,
    allowEmptyDraft = true,
    submitAdornment,
    onSlashQuery,
    onAtQuery,
    slashColor,
    atColor,
    hashColor,
    enableHashDatePicker = false,
    hashDateFormat = 'YYYY-MM-DD',
}, ref) => {
    const {
        agentGatewayService,
        sessionStateManagerService,
        showModelSelector,
        showSessionInfo,
        showToolCalls,
        setShowToolCalls,
        friendlyMode,
        setFriendlyMode,
    } = useAiChatServices();

    const theme = useTheme();
    const [draft, setDraft] = useState('');
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    const [sending, setSending] = useState(false);
    const [sessionDialogOpen, setSessionDialogOpen] = useState(false);
    const inputRef = useRef<HTMLTextAreaElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);

    // Image attachments — local-only state. Each attachment carries
    // its own data URL (used both for preview and for embedding into
    // the outgoing message), the underlying mime type, and a display
    // name surfaced in tooltips when the user hovers a thumbnail.
    type Attachment = { id: string; dataUrl: string; mimeType: string; name: string };
    const [attachments, setAttachments] = useState<Attachment[]>([]);
    const [isDraggingOver, setIsDraggingOver] = useState(false);
    // Lightbox state: when set, render the full-size image in a modal
    // overlay. We hold the whole Attachment (not just an id) so the
    // preview keeps showing even if the user removes the entry from
    // the row mid-preview.
    const [previewAttachment, setPreviewAttachment] = useState<Attachment | null>(null);
    const dragCounterRef = useRef(0);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const iconColor = theme.palette.mode == 'light' ? theme.palette.text.primary : theme.palette.primary.main;
    const themeMode = theme.palette.mode;

    // Read a list of files (from a drop, paste, or file picker) and
    // append the image entries as data URLs. Non-image files are
    // ignored — this prompt only accepts images today.
    const addFiles = useCallback(async (files: FileList | File[]) => {
        const list = Array.from(files).filter((f) => f.type.startsWith('image/'));
        if (list.length === 0) return;
        const next = await Promise.all(
            list.map(
                (file) =>
                    new Promise<Attachment>((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onload = () =>
                            resolve({
                                id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                                dataUrl: String(reader.result ?? ''),
                                mimeType: file.type,
                                name: file.name || 'imagem',
                            });
                        reader.onerror = () => reject(reader.error);
                        reader.readAsDataURL(file);
                    }),
            ),
        );
        setAttachments((prev) => [...prev, ...next]);
    }, []);

    const removeAttachment = useCallback((id: string) => {
        setAttachments((prev) => prev.filter((a) => a.id !== id));
    }, []);

    const openFilePicker = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    // ── Trigger autocomplete ('/' and '@') ─────────────────────────
    // Hosts wire `onSlashQuery` / `onAtQuery` to surface items as the
    // user types a `/`- or `@`-led token. We track the active trigger
    // (char + query + token bounds), the fetched items, the floating
    // popover's caret-aligned coords, and the keyboard highlight.
    type ActiveTrigger = {
        trigger: '/' | '@' | '#';
        query: string;
        startIdx: number; // index of the trigger char within `draft`
        endIdx: number;   // current caret position (end of query)
    };
    const [activeTrigger, setActiveTrigger] = useState<ActiveTrigger | null>(null);
    const [triggerItems, setTriggerItems] = useState<PromptTriggerItem[]>([]);
    const [triggerLoading, setTriggerLoading] = useState(false);
    const [triggerHighlight, setTriggerHighlight] = useState(0);
    const [caretCoords, setCaretCoords] = useState<{ top: number; left: number } | null>(null);
    // Discard out-of-order async responses (each fetch bumps the seq;
    // any resolution from an older seq is dropped).
    const triggerFetchSeqRef = useRef(0);
    const triggerDebounceRef = useRef<number | null>(null);
    // Stash the latest fetcher props in a ref so the fetch effect can
    // depend ONLY on the active trigger (char + query) and not on the
    // callback identities. Inline arrow-function props are the common
    // case for consumers; if we keyed the effect on their reference,
    // any state change inside the host that re-creates them would
    // re-fire the debounce, the new fetch would update state in the
    // host, that would re-create the callbacks again — infinite loop.
    const triggerFetchersRef = useRef({ onSlashQuery, onAtQuery });
    triggerFetchersRef.current = { onSlashQuery, onAtQuery };

    // Highlight overlay — opt-in via `slashColor` / `atColor`. When
    // either is set, the textarea text goes transparent and a sibling
    // mirror paints the chip-styled tokens behind it. The mirror's
    // content is translated on textarea scroll so multi-line input
    // stays in lockstep.
    const highlightActive = Boolean(slashColor || atColor || hashColor);
    const highlightTokens = useMemo(
        () => (highlightActive ? tokenizeForHighlight(draft) : []),
        [highlightActive, draft],
    );
    const highlightContentRef = useRef<HTMLDivElement | null>(null);
    // Off-screen mirror element used to measure the caret's pixel
    // position inside the textarea. Created lazily once and reused —
    // we copy the textarea's computed styles into it on each measure
    // because they change with theme / `inputMinHeight`.
    const caretMirrorRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const mirror = document.createElement('div');
        mirror.setAttribute('aria-hidden', 'true');
        Object.assign(mirror.style, {
            position: 'absolute',
            top: '0',
            left: '-9999px',
            visibility: 'hidden',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
        } as CSSStyleDeclaration);
        document.body.appendChild(mirror);
        caretMirrorRef.current = mirror;
        return () => {
            document.body.removeChild(mirror);
            caretMirrorRef.current = null;
        };
    }, []);

    // Walk back from the caret looking for the `/` or `@` that opens
    // the current token. The trigger must lead its own token (start of
    // string or preceded by whitespace) — that's what stops us from
    // matching the `/` in `http://…` or the `@` in `user@host`.
    //
    // The `@` trigger (mentions/locations) may span a couple of REGULAR
    // spaces so multi-word names ("@sao pa") don't close the popover on
    // the first space; `/` and `#` stay single-token. A non-breaking
    // space is always a hard boundary — it joins the words of an already-
    // inserted mention, so a completed "@São Paulo" won't re-open it.
    const MAX_MENTION_SPACES = 2;
    const detectTrigger = useCallback((value: string, caretPos: number): ActiveTrigger | null => {
        let spaces = 0;
        for (let i = caretPos - 1; i >= 0; i -= 1) {
            const ch = value[i];
            if (ch === '/' || ch === '@' || ch === '#') {
                const atBoundary = i === 0 || /\s/.test(value[i - 1]);
                if (!atBoundary) return null;
                // Only '@' may carry spaces; a spaced '/'-command or '#'-token has already ended.
                if (ch !== '@' && spaces > 0) return null;
                return {
                    trigger: ch as '/' | '@' | '#',
                    query: value.slice(i + 1, caretPos),
                    startIdx: i,
                    endIdx: caretPos,
                };
            }
            if (ch === '\u00A0') return null;                 // NBSP: hard boundary (completed mention)
            if (ch === ' ' || ch === '\t') {                  // regular space: allowed for '@', up to the cap
                spaces += 1;
                if (spaces > MAX_MENTION_SPACES) return null;
                continue;
            }
            if (/\s/.test(ch)) return null;                   // newline / other whitespace: boundary
        }
        return null;
    }, []);

    // Measure the pixel coordinates of the caret by syncing a mirrored
    // div to the textarea's visual layout, dropping the same prefix
    // into it, and reading the bounding rect of a zero-width marker
    // span at the end. Returns viewport coordinates of the *bottom* of
    // the current text line so the popover can sit just under it.
    const measureCaret = useCallback((textarea: HTMLTextAreaElement, value: string, caretPos: number) => {
        const mirror = caretMirrorRef.current;
        if (!mirror) return null;
        const computed = window.getComputedStyle(textarea);
        const props = [
            'boxSizing', 'width', 'height',
            'borderTopWidth', 'borderRightWidth', 'borderBottomWidth', 'borderLeftWidth',
            'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
            'fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'lineHeight',
            'letterSpacing', 'textTransform', 'textIndent',
        ] as const;
        for (const prop of props) {
            (mirror.style as unknown as Record<string, string>)[prop] = (computed as unknown as Record<string, string>)[prop];
        }
        mirror.textContent = value.slice(0, caretPos);
        const marker = document.createElement('span');
        marker.textContent = '​';
        mirror.appendChild(marker);
        const taRect = textarea.getBoundingClientRect();
        const markerRect = marker.getBoundingClientRect();
        const mirrorRect = mirror.getBoundingClientRect();
        mirror.removeChild(marker);
        const lineHeight = parseFloat(computed.lineHeight) || parseFloat(computed.fontSize) * 1.5;
        return {
            top: taRect.top + (markerRect.top - mirrorRect.top) - textarea.scrollTop + lineHeight,
            left: taRect.left + (markerRect.left - mirrorRect.left) - textarea.scrollLeft,
        };
    }, []);

    // Debounced fetch. Re-runs whenever the trigger char or the query
    // changes; cancels in-flight timers; drops stale responses via the
    // seq counter. When `activeTrigger` clears we wipe items so the
    // popover unmounts cleanly.
    useEffect(() => {
        if (!activeTrigger) {
            setTriggerItems([]);
            setTriggerLoading(false);
            if (triggerDebounceRef.current != null) {
                window.clearTimeout(triggerDebounceRef.current);
                triggerDebounceRef.current = null;
            }
            return;
        }
        // `#` is its own thing — the popover hosts a date picker, not
        // a fetched items list. We still want the active-trigger state
        // (so the popover anchors / chip styles light up) but skip the
        // fetch path entirely.
        if (activeTrigger.trigger === '#') {
            setTriggerItems([]);
            setTriggerLoading(false);
            return;
        }
        const fetcher = activeTrigger.trigger === '/'
            ? triggerFetchersRef.current.onSlashQuery
            : triggerFetchersRef.current.onAtQuery;
        if (!fetcher) {
            setTriggerItems([]);
            return;
        }
        if (triggerDebounceRef.current != null) {
            window.clearTimeout(triggerDebounceRef.current);
        }
        triggerDebounceRef.current = window.setTimeout(async () => {
            const seq = ++triggerFetchSeqRef.current;
            setTriggerLoading(true);
            try {
                // Re-read from the ref at call time so a consumer that
                // hot-swaps the fetcher between the debounce schedule
                // and the fire still hits the latest implementation.
                const liveFetcher = activeTrigger.trigger === '/'
                    ? triggerFetchersRef.current.onSlashQuery
                    : triggerFetchersRef.current.onAtQuery;
                if (!liveFetcher) return;
                const items = await liveFetcher(activeTrigger.query);
                if (seq !== triggerFetchSeqRef.current) return;
                setTriggerItems(items);
                setTriggerHighlight(0);
            } catch (err) {
                console.error('[PromptV2] trigger query failed', err);
                if (seq !== triggerFetchSeqRef.current) return;
                setTriggerItems([]);
            } finally {
                if (seq === triggerFetchSeqRef.current) setTriggerLoading(false);
            }
        }, 200);
        return () => {
            if (triggerDebounceRef.current != null) {
                window.clearTimeout(triggerDebounceRef.current);
                triggerDebounceRef.current = null;
            }
        };
    }, [activeTrigger?.trigger, activeTrigger?.query]);

    const insertTriggerItem = useCallback((item: PromptTriggerItem) => {
        if (!activeTrigger || !inputRef.current) return;
        const textarea = inputRef.current;
        const before = draft.slice(0, activeTrigger.startIdx);
        const after = draft.slice(activeTrigger.endIdx);
        const next = `${before}${item.value} ${after}`;
        const caret = (before + item.value + ' ').length;
        setDraft(next);
        setActiveTrigger(null);
        setTriggerItems([]);
        setCaretCoords(null);
        // Defer to next frame so React commits the new value before we
        // move the caret — otherwise setSelectionRange runs against the
        // pre-commit DOM and gets clobbered.
        requestAnimationFrame(() => {
            textarea.focus();
            textarea.setSelectionRange(caret, caret);
        });
    }, [activeTrigger, draft]);

    // Splice the picked date in as `#${formattedDate}`. Mirrors
    // `insertTriggerItem`'s mechanics — the chip styling kicks in
    // automatically because the tokenizer treats `#token` like any
    // other trigger run.
    const insertDate = useCallback((date: Dayjs | null) => {
        if (!date || !activeTrigger || !inputRef.current) return;
        const textarea = inputRef.current;
        const formatted = `#${date.format(hashDateFormat)}`;
        const before = draft.slice(0, activeTrigger.startIdx);
        const after = draft.slice(activeTrigger.endIdx);
        const next = `${before}${formatted} ${after}`;
        const caret = (before + formatted + ' ').length;
        setDraft(next);
        setActiveTrigger(null);
        setCaretCoords(null);
        requestAnimationFrame(() => {
            textarea.focus();
            textarea.setSelectionRange(caret, caret);
        });
    }, [activeTrigger, draft, hashDateFormat]);

    // Virtual anchor for Popper — a 0×0 rect at the caret coords.
    // Popper only reads `getBoundingClientRect`, so this satisfies
    // its VirtualElement contract.
    const triggerAnchor = useMemo(() => {
        if (!caretCoords) return null;
        const { top, left } = caretCoords;
        return {
            getBoundingClientRect: () => ({
                top, left, bottom: top, right: left,
                width: 0, height: 0, x: left, y: top,
                toJSON: () => ({}),
            }) as DOMRect,
        };
    }, [caretCoords]);

    const syncTriggerFromTextarea = useCallback((ta: HTMLTextAreaElement) => {
        const value = ta.value;
        const caret = ta.selectionStart ?? value.length;
        const detected = detectTrigger(value, caret);
        setActiveTrigger(detected);
        if (detected) {
            const coords = measureCaret(ta, value, caret);
            if (coords) setCaretCoords(coords);
        } else {
            setCaretCoords(null);
        }
    }, [detectTrigger, measureCaret]);

    const isTurnOpen = useAiChatSessionStore((s) => s.turnOpen);
    const setTurnOpen = useAiChatSessionStore((s) => s.setTurnOpen);
    const connectionStatus = useAiChatSessionStore((s) => s.connectionStatus);
    const isConnected = connectionStatus === ConnectionState.Connected;
    // Shared suspend state — same value RunProgressPanelV2 reads and
    // writes, so pausing in either surface flips the icon cluster in
    // both. Reset to `idle` happens in the store when the turn closes
    // (`setTurnOpen(false)`), so we don't have to mirror that here.
    const suspendState = useAiChatSessionStore((s) => s.suspendState);
    const setSuspendState = useAiChatSessionStore((s) => s.setSuspendState);
    const isSuspended = suspendState === 'suspended';
    const isSuspending = suspendState === 'suspending';

    const handleToggleConnection = useCallback((checked: boolean) => {
        if (!(agentGatewayService instanceof AgentGatewayWSAsync)) return;
        if (checked) {
            sessionStateManagerService.bindAndOpenLiveSession(
                agentGatewayService.getAgentContext(),
                agentGatewayService.getSessionId(),
            );
        } else {
            sessionStateManagerService.closeLiveConnection(true, false);
        }
    }, [agentGatewayService, sessionStateManagerService]);

    const resolvedMinHeight = inputMinHeight ?? TEXTAREA_MIN_HEIGHT;

    // Auto-grow the textarea up to TEXTAREA_MAX_HEIGHT, never below
    // the resolved min. The min is what makes the splash composer feel
    // tall (no sketch yet → composer passes a bigger floor).
    const fit = (el: HTMLTextAreaElement | null) => {
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${Math.min(
            Math.max(el.scrollHeight / 2, resolvedMinHeight / (topSlotExpanded ? 5 : 1)),
            TEXTAREA_MAX_HEIGHT,
        )}px`;
    };
    useEffect(() => {
        fit(inputRef.current);
    }, [draft, resolvedMinHeight, topSlotExpanded]);

    useEffect(() => {
        setDraft(seedDraft ?? '');
    }, [seedDraft]);

    // Surface row height to ChatPane (matches the v1 contract).
    useEffect(() => {
        if (!containerRef.current) return;
        const observer = new ResizeObserver(() => {
            if (containerRef.current && onHeightChange) {
                onHeightChange(containerRef.current.getBoundingClientRect().height);
            }
        });
        observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, [onHeightChange]);

    const handleSend = async (textOverride?: string) => {
        // Normalize non-breaking spaces (U+00A0) back to regular spaces: hosts can
        // join a multi-word `@mention` with NBSPs so the highlighter keeps it as one
        // chip; the sent message should still carry plain spaces.
        const text = (textOverride ?? draft).trim().replace(/\u00A0/g, ' ');
        // Attachments are part of this user's intent only when we're
        // sending the locally-typed draft — `forceMessage`-style auto
        // sends from outside the component (suggestion clicks, etc.)
        // shouldn't sweep up images the user hadn't yet committed.
        const usingDraft = textOverride === undefined;
        const pendingAttachments = usingDraft ? attachments : [];
        // Honor the same `allowEmptyDraft` opt-out the disabled state
        // uses — otherwise the button is enabled but clicking it
        // silently bails here, which is what was happening when the
        // composer had queued sketch changes but no typed prompt.
        // Attachments also count as content: empty text + an image is
        // a perfectly valid submission.
        if (!text && !allowEmptyDraft && pendingAttachments.length === 0) return;
        // Sending while a turn is open is normally invalid — but a
        // *suspended* turn is the exception: the user has paused the
        // agent and typing a follow-up is the documented way to
        // "continue with new context". The gateway's
        // `sendMessageInternal` already clears `suspendState` and
        // starts a new interaction (with `wasSuspended=true` carried
        // through for the agent's context); we mirror that on the
        // client side just below so our cluster flips back to "idle"
        // immediately instead of waiting for the next turn-open echo.
        if (sending || (isTurnOpen && !isSuspended)) return;
        // The gateway connection is irrelevant when the host overrides
        // submit (e.g. the home splash navigates instead of sending),
        // so only enforce it on the gateway path. Bailing here BEFORE
        // we clear `draft`/`attachments` is intentional: when the user
        // clicks send mid-handshake (status is `Connecting`, not yet
        // `Connected`) we want the typed text and attached images to
        // survive the click so they can retry once the socket settles.
        // Previously this only caught `Idle`, so `Connecting` slipped
        // through, the state-clear ran, and the user lost their image
        // attachments before the send even left the page.
        if (!onSubmit && connectionStatus !== ConnectionState.Connected) return;
        if (isSuspended) setSuspendState('idle');
        setDraft('')
        if (usingDraft) setAttachments([]);
        setSending(true);
        try {
            // Strip `data:<mime>;base64,` prefix so the wire payload
            // is raw base64 — the server hands it to
            // `Convert.FromBase64String` directly.
            const clientImages = pendingAttachments.length > 0
                ? pendingAttachments.map((a) => {
                    const commaIdx = a.dataUrl.indexOf(',');
                    const base64 = commaIdx >= 0 ? a.dataUrl.slice(commaIdx + 1) : a.dataUrl;
                    return { mimeType: a.mimeType, data: base64 };
                })
                : undefined;

            // Override path: replace the gateway send entirely. We skip
            // `onBeforeSend` here on purpose — it exists to set up state
            // for the gateway (e.g. createDraft so partials autosave),
            // which is meaningless when the gateway isn't being called.
            if (onSubmit) {
                try {
                    // `onSubmit` is text-only by contract (used by the
                    // home splash to navigate). Images would be lost
                    // here regardless — the host doesn't currently have
                    // a path to forward them.
                    await onSubmit(text);
                } catch (err) {
                    console.error('[PromptV2] onSubmit failed', err);
                }
                if (inputRef.current) inputRef.current.value = '';
                return;
            }

            // Hosts can run async setup (e.g. createDraft) before the
            // message hits the gateway. If the hook throws we surface
            // it to the console and bail — sending half-prepped state
            // is worse than skipping the turn.
            if (onBeforeSend) {
                try {
                    await onBeforeSend(text);
                } catch (err) {
                    console.error('[PromptV2] onBeforeSend failed', err);
                    return;
                }
            }
            await agentGatewayService.sendMessage(new ClientMessage(text, 'text/plain', undefined, undefined, clientImages));
            if (inputRef.current) inputRef.current.value = '';
        } finally {
            setSending(false);
        }
    };

    // External `forceMessage` (e.g. clicking a suggested prompt) — replicates
    // the v1 Prompt's behavior of auto-sending when this prop changes.
    useEffect(() => {
        if (forceMessage) handleSend(forceMessage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [forceMessage]);

    const handleSelectFromHistory = useCallback((text: string) => {
        setDraft(text);
        // Defer focus so the Popover finishes its close transition first.
        requestAnimationFrame(() => {
            inputRef.current?.focus();
            const len = text.length;
            inputRef.current?.setSelectionRange(len, len);
        });
    }, []);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        // Hash trigger uses a date picker, not a list — only Escape is
        // a meaningful textarea-level handler here. Date navigation
        // happens via mouse / picker-internal keys when focus is in
        // the calendar grid.
        if (activeTrigger?.trigger === '#' && enableHashDatePicker) {
            if (e.key === 'Escape') {
                e.preventDefault();
                setActiveTrigger(null);
                setCaretCoords(null);
                return;
            }
        }
        // Autocomplete owns navigation keys while it has items to
        // show — Enter selects an item instead of submitting the
        // prompt, ArrowUp/Down move the highlight, Escape dismisses.
        // We gate on `triggerItems.length` (not just `activeTrigger`)
        // so an empty result set doesn't swallow Enter.
        if (activeTrigger && triggerItems.length > 0) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setTriggerHighlight((i) => (i + 1) % triggerItems.length);
                return;
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault();
                setTriggerHighlight((i) => (i - 1 + triggerItems.length) % triggerItems.length);
                return;
            }
            if (e.key === 'Enter' || e.key === 'Tab') {
                e.preventDefault();
                const item = triggerItems[triggerHighlight];
                if (item) insertTriggerItem(item);
                return;
            }
            if (e.key === 'Escape') {
                e.preventDefault();
                setActiveTrigger(null);
                setCaretCoords(null);
                return;
            }
        }
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            void handleSend();
        }
    };

    // Connection state only gates the gateway path — when the host
    // overrides submit, the gateway is irrelevant and we treat the
    // prompt as always available for typing.
    const isDisabled = !onSubmit && connectionStatus === ConnectionState.Idle;
    // `canSendOverGateway` is the stricter sibling of `isDisabled`: the
    // textarea stays writable during the connect handshake so the user
    // can keep typing, but the send button + handleSend guard refuse
    // any non-Connected state. Kept separate from `isDisabled` because
    // disabling the textarea while connecting feels like the app froze.
    const canSendOverGateway = !!onSubmit || connectionStatus === ConnectionState.Connected;
    // Socket handshake in flight — the AiChatProvider opens the WS on
    // mount and the gateway typically becomes `Connected` in under a
    // second, but a slow network can stretch that to 1–3s. During that
    // window the send button has nothing to send through, so swap the
    // paper-plane glyph for a spinner so the user sees "we're getting
    // ready" instead of a disabled button that looks broken. Only the
    // gateway path needs this: hosts that hand us an `onSubmit`
    // override (e.g. the home splash routing the text into a fresh
    // session) don't care about the WS at all.
    const isConnecting = !onSubmit && connectionStatus !== ConnectionState.Connected;
    // Empty-text gate is bypassed when the host (composer) tells us
    // there's other content (sketch edits) that justifies the send.
    // `isTurnOpen && !isSuspended` mirrors the early return in
    // `handleSend`: the prompt is only "locked" while the agent is
    // actively running; a suspended turn is interactive again so we
    // re-enable the send button.
    const sendDisabled = !canSendOverGateway || (isTurnOpen && !isSuspended) || sending || (!allowEmptyDraft && !draft.trim() && attachments.length === 0);

    // Placeholder is hidden when the host is showing an expanded
    // top-slot — that surface dominates the visual hierarchy and the
    // empty hint underneath is just noise.
    const placeholder = topSlot && topSlotExpanded
        ? ''
        : (placeholderText ?? 'Pergunte sobre seus dados\u2026');

    const handleToggleTopSlot = () => {
        onTopSlotExpandedChange?.(!topSlotExpanded);
    };

    // Border radius adapts to content: a true pill (999) when there's
    // only the input row; a softly rounded card (16) when the prompt
    // grows to host a sketch — fully-rounded ends look awkward on a
    // tall multi-line surface.
    const pillBorderRadius = topSlot ? 2 : borderRadius ?? 999;

    return (
        <Box
            ref={containerRef}
            sx={{
                px: 0,
                py: 1.25,
                // Transparent wrapper — the pill below has its own
                // `surface.raised` background, so the wrapper's only job
                // is to add padding around the pill. Painting
                // `background.default` here used to make sense (matched
                // the page canvas in DataExplorer / Composer), but it
                // also painted a near-black band when this prompt is
                // mounted inside `AiChatFloatingV2` (whose body is
                // `background.paper`, slightly lifted). Going
                // transparent lets every host surface show through
                // correctly without a per-host override.
                backgroundColor: 'transparent',
            }}
        >
            <Box
                onDragEnter={(e) => {
                    if (!e.dataTransfer.types?.includes('Files')) return;
                    e.preventDefault();
                    dragCounterRef.current += 1;
                    setIsDraggingOver(true);
                }}
                onDragOver={(e) => {
                    if (!e.dataTransfer.types?.includes('Files')) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'copy';
                }}
                onDragLeave={() => {
                    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
                    if (dragCounterRef.current === 0) setIsDraggingOver(false);
                }}
                onDrop={(e) => {
                    e.preventDefault();
                    dragCounterRef.current = 0;
                    setIsDraggingOver(false);
                    if (e.dataTransfer.files?.length) void addFiles(e.dataTransfer.files);
                }}
                sx={(theme) => ({
                    position: 'relative',
                    backgroundColor: alpha(theme.palette.background.default,0.9),
                    border: `1px solid ${isDraggingOver ? theme.palette.primary.main : theme.palette.primary.dark}`,
                    borderRadius: attachments.length > 0 ? 3 : pillBorderRadius,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'border-color .12s, border-radius .18s, box-shadow .12s',
                    boxShadow: isDraggingOver
                        ? `0 0 0 3px ${alpha(theme.palette.primary.main, 0.18)}`
                        : 'none',
                    '&:focus-within': {
                        borderColor: theme.palette.primary.dark,
                    },
                    '&:hover .v2-prompt-topSlot-chevron': {
                        opacity: 1,
                        pointerEvents: 'auto',
                    },
                    '& textarea::placeholder': {
                        color: theme.palette.text.disabled,
                    },
                    // When the highlight overlay is on, the textarea
                    // glyphs are transparent — without this rule the
                    // selection rectangle would paint over blank space.
                    // Forcing a visible color on ::selection keeps the
                    // highlighted text readable mid-selection.
                    '& textarea::selection': {
                        color: theme.palette.text.primary,
                        background: alpha(theme.palette.primary.main, 0.3),
                    },
                })}
            >
                {/* Hidden native file input — driven by the watermark
                    click. `multiple` so a user can pick a batch in one
                    go; we filter to images on read. */}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    style={{ display: 'none' }}
                    onChange={(e) => {
                        if (e.target.files?.length) void addFiles(e.target.files);
                        e.target.value = '';
                    }}
                />

                {/* Drag-over overlay. Pointer-events disabled so the
                    drop event still reaches the pill underneath — the
                    overlay is purely a visual hint that a drop will
                    land here. */}
                {isDraggingOver && (
                    <Box
                        aria-hidden
                        sx={(theme) => ({
                            position: 'absolute',
                            inset: 0,
                            zIndex: 5,
                            pointerEvents: 'none',
                            backgroundColor: alpha(theme.palette.primary.main, 0.08),
                            border: `2px dashed ${theme.palette.primary.main}`,
                            borderRadius: pillBorderRadius,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: theme.palette.primary.main,
                            fontSize: 12,
                            fontFamily: theme.typography.fontFamilyMono,
                        })}
                    >
                        Solte a imagem para anexar
                    </Box>
                )}
                {/* topSlot — sketch lives inside the pill so the prompt
                    visually IS the (sketch + text) the user is composing. */}
                {topSlot && (
                    <Box sx={{ position: 'relative' }}>
                        {topSlot}
                        {topSlotCollapsible && onTopSlotExpandedChange && (
                            <Tooltip
                                title={topSlotExpanded ? 'Recolher' : 'Expandir'}
                                placement="left"
                            >
                                <IconButton
                                    className="v2-prompt-topSlot-chevron"
                                    size="small"
                                    onClick={handleToggleTopSlot}
                                    sx={(theme) => ({
                                        position: 'absolute',
                                        top: 6,
                                        right: 6,
                                        color: theme.palette.text.secondary,
                                        backgroundColor: theme.palette.surface.raised,
                                        border: `1px solid ${theme.palette.divider}`,
                                        p: 0.4,
                                        opacity: 1,
                                        pointerEvents: 'none',
                                        zIndex: 2,
                                        transition: 'opacity .12s, color .12s, background-color .12s',
                                        transform: topSlotExpanded ? 'rotate(0deg)' : 'rotate(180deg)',
                                        '&:hover': {
                                            color: theme.palette.text.primary,
                                            backgroundColor: theme.palette.surface.hover,
                                            borderColor: theme.palette.primary.main,
                                        },
                                    })}
                                    aria-label={topSlotExpanded ? 'Recolher' : 'Expandir'}
                                >
                                    <ChevronIcon size={13} />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                )}

                {/* Hairline divider between the sketch and the input
                    row — gives the prompt a clear "header / body"
                    structure without an extra border drawn elsewhere. */}
                {topSlot && (
                    <Box
                        aria-hidden
                        sx={(theme) => ({
                            height: '1px',
                            background: theme.palette.border.soft,
                            flexShrink: 0,
                        })}
                    />
                )}

                {/* Attachments preview row. Sits inside the pill so
                    the prompt visually contains "everything that will
                    be sent" — text, sketch, and now images. Each
                    thumbnail has a hover-revealed remove button. */}
                {attachments.length > 0 && (
                    <Box
                        sx={(theme) => ({
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 0.75,
                            px: 1.25,
                            py: 1,
                            borderBottom: `1px solid ${theme.palette.border.soft}`,
                        })}
                    >
                        {attachments.map((att) => (
                            <Tooltip key={att.id} title={att.name} placement="top">
                                <Box
                                    role="button"
                                    tabIndex={0}
                                    aria-label={`Visualizar ${att.name}`}
                                    onClick={() => setPreviewAttachment(att)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            setPreviewAttachment(att);
                                        }
                                    }}
                                    sx={(theme) => ({
                                        position: 'relative',
                                        width: 56,
                                        height: 56,
                                        borderRadius: 1,
                                        overflow: 'hidden',
                                        border: `1px solid ${theme.palette.divider}`,
                                        backgroundColor: theme.palette.surface.hover,
                                        cursor: 'zoom-in',
                                        transition: 'border-color .12s',
                                        '&:hover': {
                                            borderColor: theme.palette.primary.main,
                                        },
                                        '&:focus-visible': {
                                            outline: `2px solid ${theme.palette.primary.main}`,
                                            outlineOffset: 1,
                                        },
                                        '&:hover .v2-prompt-att-remove': {
                                            opacity: 1,
                                            pointerEvents: 'auto',
                                        },
                                    })}
                                >
                                    <Box
                                        component="img"
                                        src={att.dataUrl}
                                        alt={att.name}
                                        sx={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            display: 'block',
                                        }}
                                    />
                                    <IconButton
                                        className="v2-prompt-att-remove"
                                        size="small"
                                        // Stop the click from bubbling up to
                                        // the thumbnail's zoom handler —
                                        // otherwise removing an attachment
                                        // also opens the preview for an
                                        // entry that's already gone.
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            removeAttachment(att.id);
                                        }}
                                        sx={(theme) => ({
                                            position: 'absolute',
                                            top: 2,
                                            right: 2,
                                            p: 0.25,
                                            opacity: 0,
                                            pointerEvents: 'none',
                                            transition: 'opacity .12s',
                                            backgroundColor: alpha(theme.palette.background.default, 0.85),
                                            color: theme.palette.text.primary,
                                            '&:hover': {
                                                backgroundColor: theme.palette.background.default,
                                            },
                                        })}
                                        aria-label={`Remover ${att.name}`}
                                    >
                                        <CloseIcon sx={{ fontSize: 12 }} />
                                    </IconButton>
                                </Box>
                            </Tooltip>
                        ))}
                    </Box>
                )}

                {/* Input row. Watermark sits in the top-right corner of
                    this region (so it tracks the active typing area
                    regardless of whether the sketch is expanded). The
                    pt is bumped slightly to leave room for the watermark
                    above the controls. */}
                <Box
                    sx={{
                        position: 'relative',
                        display: 'flex',
                        // Center the action icons against the textarea
                        // so they don't bottom-stick when the prompt
                        // grows (splash mode) or shrinks (execution).
                        alignItems: 'center',
                        gap: 0.75,
                        px: 1.25,
                        pt: 1.5,
                        pb: 0.75,
                    }}
                >
                    <Typography
                        component="span"
                        role="button"
                        tabIndex={0}
                        onClick={openFilePicker}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                openFilePicker();
                            }
                        }}
                        sx={(theme) => ({
                            position: 'absolute',
                            top: 4,
                            right: 30,
                            fontSize: 10,
                            color: theme.palette.text.disabled,
                            fontFamily: theme.typography.fontFamilyMono,
                            opacity: 0.5,
                            cursor: 'pointer',
                            userSelect: 'none',
                            transition: 'opacity .15s, color .15s',
                            '&:hover': {
                                opacity: 1,
                                color: theme.palette.text.secondary,
                            },
                        })}
                    >
                        Arraste, cole ou clique para anexar imagem
                    </Typography>

                    {showConfigOptions &&
                    <Tooltip title="Opções" placement="top">
                        <IconButton
                            size="small"
                            onClick={(e) => setMenuAnchor(e.currentTarget)}
                            sx={(theme) => ({
                                color: iconColor,
                                p: 0.75,
                                flexShrink: 0,
                                '&:hover': { backgroundColor: theme.palette.surface.hover },
                            })}
                            aria-label="Opções"
                        >
                            <SparkleIcon size={16} />
                        </IconButton>
                    </Tooltip>}

                    {showLastPrompts && (
                        <LastPromptsV2 onPromptSelected={handleSelectFromHistory} />
                    )}

                    {inputPrefix}

                    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', position: 'relative' }}>
                        {highlightActive && (
                            <Box
                                aria-hidden
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    pointerEvents: 'none',
                                    overflow: 'hidden',
                                }}
                            >
                                {/* Inner content layer — same font /
                                    padding / wrapping rules as the
                                    textarea, so each token sits at
                                    the same pixel as its underlying
                                    character. We transform this layer
                                    (not the outer) on textarea scroll
                                    so the clipped frame stays put. */}
                                <Box
                                    ref={highlightContentRef}
                                    sx={(theme) => ({
                                        padding: '4px 4px',
                                        fontFamily: theme.typography.fontFamily,
                                        fontSize: 13,
                                        lineHeight: 1.5,
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        // The textarea is the
                                        // transparent layer — the
                                        // overlay paints EVERYTHING
                                        // the user sees: plain text in
                                        // normal color, trigger tokens
                                        // in their chip styling.
                                        color: theme.palette.text.primary,
                                        willChange: 'transform',
                                    })}
                                >
                                    {highlightTokens.map((tok, idx) => {
                                        if (tok.type === 'plain') {
                                            // Wrap plain text in a
                                            // span so React doesn't
                                            // merge adjacent text
                                            // nodes (which could shift
                                            // wrapping).
                                            return <span key={idx}>{tok.text}</span>;
                                        }
                                        const color = tok.type === 'slash'
                                            ? slashColor
                                            : tok.type === 'at'
                                                ? atColor
                                                : hashColor;
                                        if (!color) {
                                            return <span key={idx}>{tok.text}</span>;
                                        }
                                        return (
                                            <span
                                                key={idx}
                                                style={{
                                                    color,
                                                    backgroundColor: alpha(color, 0.14),
                                                    // Inset box-shadow
                                                    // draws the chip
                                                    // border WITHOUT
                                                    // changing the
                                                    // span's footprint
                                                    // — critical for
                                                    // keeping overlay
                                                    // and textarea
                                                    // glyph positions
                                                    // aligned.
                                                    boxShadow: `inset 0 0 0 1px ${alpha(color, 0.45)}`,
                                                    borderRadius: 4,
                                                    fontWeight: 500,
                                                }}
                                            >
                                                {tok.text}
                                            </span>
                                        );
                                    })}
                                    {/* Trailing zero-width char keeps
                                        the final empty line tall — a
                                        bare trailing newline in the
                                        textarea otherwise collapses
                                        in the overlay. */}
                                    <span>{'​'}</span>
                                </Box>
                            </Box>
                        )}
                        <textarea
                            ref={(el: HTMLTextAreaElement | null) => {
                                inputRef.current = el;
                            }}
                            rows={1}
                            placeholder={placeholder}
                            value={draft}
                            disabled={isDisabled}
                            // Spellcheck underlines (wavy red lines)
                            // sit on top of the transparent glyphs and
                            // bleed through to the user as misaligned
                            // squiggles when the overlay is on. Turn
                            // them off in that mode; leave the default
                            // behaviour intact when the overlay isn't
                            // mounted.
                            spellCheck={highlightActive ? false : undefined}
                            onChange={(e) => {
                                setDraft(e.target.value);
                                syncTriggerFromTextarea(e.target);
                            }}
                            // `onSelect` covers caret moves that don't
                            // change the value (arrow keys, clicks) —
                            // without it the popover would drift out of
                            // sync if the user navigated into / out of
                            // an existing trigger token.
                            onSelect={(e) => syncTriggerFromTextarea(e.currentTarget)}
                            onBlur={() => {
                                // Closing on blur keeps the popover from
                                // hanging over the page after the user
                                // tabs away. Mouse clicks on items
                                // preventDefault on mousedown so the
                                // textarea never loses focus first.
                                setActiveTrigger(null);
                                setCaretCoords(null);
                            }}
                            onKeyDown={handleKeyDown}
                            onScroll={highlightActive ? (e) => {
                                // Keep the overlay's text in lockstep
                                // with the textarea's scrollTop. Done
                                // imperatively (DOM mutation, not
                                // state) so multi-line paste / fast
                                // scroll doesn't pay a re-render cost.
                                if (highlightContentRef.current) {
                                    highlightContentRef.current.style.transform =
                                        `translateY(${-e.currentTarget.scrollTop}px)`;
                                }
                            } : undefined}
                            onPaste={(e) => {
                                // Pasted images come through as
                                // `clipboardData.items` of kind "file".
                                // We only intercept when there's at
                                // least one image in the payload —
                                // text-only pastes fall through to the
                                // textarea's default behaviour.
                                const items = e.clipboardData?.items;
                                if (!items) return;
                                const files: File[] = [];
                                for (let i = 0; i < items.length; i += 1) {
                                    const it = items[i];
                                    if (it.kind === 'file' && it.type.startsWith('image/')) {
                                        const f = it.getAsFile();
                                        if (f) files.push(f);
                                    }
                                }
                                if (files.length > 0) {
                                    e.preventDefault();
                                    void addFiles(files);
                                }
                            }}
                            style={{
                                width: '100%',
                                minHeight: resolvedMinHeight,
                                maxHeight: TEXTAREA_MAX_HEIGHT,
                                background: 'transparent',
                                border: 'none',
                                outline: 'none',
                                resize: 'none',
                                // When the overlay is on, the textarea
                                // glyphs go transparent so the chip-
                                // styled mirror behind it is the only
                                // thing the user sees — but we still
                                // need a colored caret, otherwise the
                                // textarea looks "uneditable". `color:
                                // transparent` would normally hide the
                                // caret too; `caretColor` brings it
                                // back independently.
                                color: highlightActive ? 'transparent' : theme.palette.text.primary,
                                caretColor: theme.palette.text.primary,
                                position: highlightActive ? 'relative' : undefined,
                                zIndex: highlightActive ? 1 : undefined,
                                fontFamily: theme.typography.fontFamily,
                                fontSize: 13,
                                lineHeight: 1.5,
                                padding: '4px 4px',
                                overflow: 'auto',
                                // Smooth the splash → execution morph. The
                                // `fit()` effect sets concrete px heights,
                                // and `min-height` / the resolved floor
                                // both change when the host flips
                                // `inputMinHeight` (e.g. composerMode
                                // moves out of 'empty'). Without this,
                                // the textarea jumps; with it the height
                                // tweens alongside the layoutId morph.
                                transition: 'min-height 250ms cubic-bezier(.4,0,.2,1), height 250ms cubic-bezier(.4,0,.2,1)',
                            }}
                        />
                    </Box>

                    {/* Host-supplied inline adornment (e.g. composer's
                        "queued sketch changes" chip). Only shown while
                        no turn is open — once an agent run starts, the
                        action row morphs into the pause control and
                        the adornment loses its frame of reference. */}
                    {!isTurnOpen && submitAdornment ? (
                        <Box sx={{ flexShrink: 0, marginLeft: 'auto', display: 'inline-flex', alignItems: 'center' }}>
                            {submitAdornment}
                        </Box>
                    ) : null}

                    {/* Send-button cluster — three states:
                          - idle (no turn): Send.
                          - turn-running, not suspended: Pause. Calls
                            `agentGatewayService.suspend()` and flips the
                            shared `suspendState` to 'suspending' so
                            RunProgressPanelV2's header morphs in lockstep.
                          - turn-running, suspended: Stop + Continue/Send.
                            • Stop hard-cancels via `stopStream(true)`.
                            • The send button doubles as Continue: if the
                              user has text it sends + auto-clears suspend
                              (gateway does this in `sendMessageInternal`
                              and we pre-empt our own store so the cluster
                              flips back to 'idle' immediately); if the
                              textarea is empty it calls `cancelSuspend()`
                              which finalizes the partial as the turn's
                              output without starting a new turn. */}
                    {isTurnOpen && !isSuspended ? (
                        <Tooltip title="Pausar resposta" placement="top">
                            <span style={{ flexShrink: 0, marginLeft: 'auto' }}>
                                <IconButton
                                    size="small"
                                    disabled={isSuspending}
                                    onClick={async () => {
                                        if (!(agentGatewayService instanceof AgentGatewayWSAsync)) return;
                                        try {
                                            setSuspendState('suspending');
                                            const ok = await agentGatewayService.suspend();
                                            setSuspendState(ok ? 'suspended' : 'idle');
                                        } catch (err) {
                                            console.error('[PromptV2] suspend failed', err);
                                            setSuspendState('idle');
                                        }
                                    }}
                                    sx={(theme) => ({
                                        color: `${iconColor} !important`,
                                        backgroundColor: theme.palette.surface.raised,
                                        border: `1px solid ${theme.palette.divider}`,
                                        p: 0.75,
                                        transition: 'background-color .12s, border-color .12s',
                                        '&:hover': {
                                            backgroundColor: theme.palette.surface.hover,
                                            borderColor: theme.palette.primary.main,
                                        },
                                    })}
                                    aria-label="Pausar resposta"
                                >
                                    <PauseIcon size={14} />
                                </IconButton>
                            </span>
                        </Tooltip>
                    ) : isSuspended ? (
                        <Box
                            sx={{
                                flexShrink: 0,
                                marginLeft: 'auto',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                            }}
                        >
                            <Tooltip title="Parar" placement="top">
                                <span>
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            try {
                                                setTurnOpen(false);
                                            } catch (err) {
                                                console.error('[PromptV2] stopStream failed', err);
                                            }
                                        }}
                                        sx={(theme) => ({
                                            color: `${iconColor} !important`,
                                            backgroundColor: theme.palette.surface.raised,
                                            border: `1px solid ${theme.palette.divider}`,
                                            p: 0.75,
                                            '&:hover': {
                                                backgroundColor: theme.palette.surface.hover,
                                                borderColor: theme.palette.secondary.main,
                                            },
                                        })}
                                        aria-label="Parar"
                                    >
                                        <StopIcon size={13} />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title={draft.trim() ? 'Continuar com a mensagem' : 'Continuar'} placement="top">
                                <span>
                                    <IconButton
                                        size="small"
                                        {...sendButtonProps}
                                        onClick={() => {
                                            // Empty textarea: send a
                                            // canonical "Continue" message
                                            // so the gateway clears its
                                            // own suspendState (in
                                            // `sendMessageInternal`) and
                                            // starts a new turn with that
                                            // text — the agent receives a
                                            // user message instructing it
                                            // to resume from the pause
                                            // point. Earlier this branch
                                            // called `cancelSuspend()`
                                            // which is `turnComplete` in
                                            // disguise: it FINALIZES the
                                            // partial output and closes
                                            // the turn, i.e. the opposite
                                            // of "continue".
                                            //
                                            // With typed text we just
                                            // delegate to handleSend; it
                                            // already pre-clears the
                                            // store's suspendState so the
                                            // cluster flips back to idle
                                            // immediately.
                                            void handleSend(draft.trim() ? undefined : 'Continue');
                                        }}
                                        sx={(theme) => ({
                                            color: `${theme.palette.primary.main} !important`,
                                            backgroundColor: themeMode == 'dark' ? theme.palette.surface.raised : 'unset',
                                            border: themeMode == 'dark' ? `1px solid ${theme.palette.primary.main}` : 'unset',
                                            p: 0.75,
                                            '&:hover': themeMode == 'dark' ? {
                                                backgroundColor: theme.palette.surface.hover,
                                            } : null
                                        })}
                                        aria-label="Continuar"
                                    >
                                        {themeMode == "dark"
                                        ? <SendIcon size={14} />
                                        : <Stack direction={"row"} gap={1} sx={{ backgroundColor: theme.palette.primary.main, p: 1, color: iconColor, borderRadius: theme.shape.radiusSm, display: 'inline-flex', flexWrap: 'nowrap', alignItems: 'center' }}>
                                            <Typography variant='body2'>Enviar ↵</Typography>
                                            {/* <SendIcon size={14} /> */}
                                        </Stack>}
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                    ) : (
                        <Tooltip title={isConnecting ? 'Conectando…' : 'Enviar'} placement="top">
                            <span style={{ flexShrink: 0, marginLeft: 'auto' }}>
                                <IconButton
                                    size="small"
                                    {...sendButtonProps}
                                    onClick={() => void handleSend()}
                                    disabled={sendDisabled}
                                    sx={(theme) => ({
                                        color: `${iconColor} !important`,
                                        p: 0.75,
                                        // Spinner has its own motion — keep the
                                        // button at full opacity so it reads as
                                        // a live "loading" affordance instead of
                                        // the dimmed empty-draft state.
                                        opacity: isConnecting ? 1 : (sendDisabled ? 0.45 : 1),
                                        transition: 'opacity .12s',
                                        // The rotate/translate flourish is for
                                        // the paper-plane glyph; applying it to
                                        // the spinner's <svg> would tilt the
                                        // whole ring and fight its own rotation.
                                        ...(isConnecting
                                            ? {}
                                            : {
                                                '& svg': {
                                                    transform: 'translateY(-1px) rotate(-15deg)',
                                                    transition: 'transform .15s',
                                                },
                                                '&:hover svg': {
                                                    transform: 'translateY(-2px) rotate(-22deg)',
                                                },
                                            }),
                                    })}
                                    aria-label={isConnecting ? 'Conectando' : 'Enviar'}
                                >
                                    {isConnecting ? (
                                        <CircularProgress size={14} thickness={5} color="inherit" />
                                    ) : (themeMode == "dark"
                                        ? <SendIcon size={14} />
                                        : <Stack direction={"row"} gap={1} sx={{ backgroundColor: theme.palette.primary.main, px: 1.5,py:1, color: iconColor, borderRadius: theme.shape.radiusSm, display: 'inline-flex', flexWrap: 'nowrap', alignItems: 'center' }}>
                                            <Typography variant='body2'>Enviar ↵</Typography>
                                            {/* <SendIcon size={14} /> */}
                                        </Stack>)
                                    }
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}
                </Box>
            </Box>

            {/* Trigger autocomplete ('/' and '@'). Anchored to a
                virtual element at the caret coords so the popover
                follows wherever in the textarea the user is typing.
                `onMouseDown={preventDefault}` on the Paper stops the
                textarea from blurring when a row is clicked — that's
                how the click handler can still read activeTrigger /
                draft to splice the chosen item in. */}
            <Popper
                open={
                    activeTrigger !== null && triggerAnchor !== null && (
                        activeTrigger.trigger === '#'
                            ? Boolean(enableHashDatePicker)
                            : (triggerItems.length > 0 || triggerLoading)
                    )
                }
                anchorEl={triggerAnchor as never}
                placement="bottom-start"
                modifiers={[
                    { name: 'offset', options: { offset: [0, 6] } },
                    { name: 'preventOverflow', options: { padding: 8 } },
                    { name: 'flip', options: { fallbackPlacements: ['top-start'] } },
                ]}
                sx={(theme) => ({ zIndex: theme.zIndex.modal + 1 })}
            >
                <Paper
                    onMouseDown={(e) => e.preventDefault()}
                    sx={(theme) => ({
                        // Date picker needs more room than a list row;
                        // bump the min when the hash trigger is live.
                        minWidth: activeTrigger?.trigger === '#' ? 320 : 220,
                        maxWidth: activeTrigger?.trigger === '#' ? 360 : 360,
                        maxHeight: activeTrigger?.trigger === '#' ? 'none' : 280,
                        overflowY: activeTrigger?.trigger === '#' ? 'visible' : 'auto',
                        backgroundColor: theme.palette.surface.raised,
                        border: `1px solid ${theme.palette.divider}`,
                        boxShadow: theme.shadows[6],
                        py: activeTrigger?.trigger === '#' ? 0 : 0.5,
                    })}
                >
                    {activeTrigger?.trigger === '#' ? (
                        // Self-contained LocalizationProvider so the
                        // picker works even if the host app didn't
                        // mount one up the tree (e.g. in Storybook /
                        // a bare library demo).
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                            <StaticDatePicker
                                value={dayjs()}
                                onAccept={insertDate}
                                onChange={insertDate}
                                slotProps={{
                                    // Hide the OK/Cancel bar — we
                                    // commit immediately on click and
                                    // close on Escape. Toolbar (the
                                    // big date header) is hidden too;
                                    // the popover's tight width makes
                                    // it noisy.
                                    actionBar: { actions: [] },
                                    toolbar: { hidden: true },
                                }}
                                sx={{
                                    '& .MuiPickersLayout-root': {
                                        backgroundColor: 'transparent',
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    ) : (
                        <>
                            {triggerLoading && triggerItems.length === 0 && (
                                <Box
                                    sx={(theme) => ({
                                        px: 1.5,
                                        py: 1,
                                        color: theme.palette.text.disabled,
                                        fontSize: 12,
                                        fontFamily: theme.typography.fontFamilyMono,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                    })}
                                >
                                    <CircularProgress size={12} thickness={5} color="inherit" />
                                    Carregando…
                                </Box>
                            )}
                            {triggerItems.map((item, idx) => (
                                <Box
                                    key={item.id}
                                    role="option"
                                    aria-selected={idx === triggerHighlight}
                                    onClick={() => insertTriggerItem(item)}
                                    onMouseEnter={() => setTriggerHighlight(idx)}
                                    sx={(theme) => ({
                                        px: 1.5,
                                        py: 0.75,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        backgroundColor: idx === triggerHighlight
                                            ? theme.palette.surface.hover
                                            : 'transparent',
                                        transition: 'background-color .08s',
                                    })}
                                >
                                    <Typography sx={(theme) => ({ fontSize: 12, color: theme.palette.text.primary })}>
                                        {item.label}
                                    </Typography>
                                    {item.hint && (
                                        <Typography sx={(theme) => ({ fontSize: 10.5, color: theme.palette.text.disabled })}>
                                            {item.hint}
                                        </Typography>
                                    )}
                                </Box>
                            ))}
                        </>
                    )}
                </Paper>
            </Popper>

            {/* Floating menu — model selector + friendly-mode toggle. */}
            <Menu
                open={!!menuAnchor}
                anchorEl={menuAnchor}
                onClose={() => setMenuAnchor(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                slotProps={{
                    paper: {
                        sx: (theme) => ({
                            backgroundColor: theme.palette.surface.raised,
                            border: `1px solid ${theme.palette.divider}`,
                            mb: 0.5,
                            minWidth: 240,
                        }),
                    },
                }}
            >
                {showModelSelector !== false && [
                    <Box
                        key="model-section-label"
                        sx={(theme) => ({
                            px: 2,
                            pt: 1,
                            pb: 0.5,
                            fontSize: 10,
                            color: theme.palette.text.disabled,
                            textTransform: 'uppercase',
                            letterSpacing: 0.6,
                            fontWeight: 500,
                        })}
                    >
                        Modelo
                    </Box>,
                    <Box
                        key="model-selector"
                        sx={{
                            px: 1,
                            pb: 1,
                            maxWidth: 520,
                            maxHeight: 320,
                            overflowY: 'auto',
                            overflowX: 'hidden',
                        }}
                    >
                        <ModelSelector inline />
                    </Box>,
                    <Divider key="model-divider" sx={(theme) => ({ borderColor: theme.palette.border.soft })} />,
                ]}

                <Box
                    sx={(theme) => ({
                        px: 2,
                        pt: 1,
                        pb: 0.5,
                        fontSize: 10,
                        color: theme.palette.text.disabled,
                        textTransform: 'uppercase',
                        letterSpacing: 0.6,
                        fontWeight: 500,
                    })}
                >
                    Preferências
                </Box>

                <MenuItem
                    onClick={() => setFriendlyMode(!friendlyMode)}
                    sx={(theme) => ({ fontSize: 12, color: theme.palette.text.primary, py: 1 })}
                >
                    <ListItemText
                        primary="Modo amigável"
                        secondary={friendlyMode ? 'Mostra rótulos legíveis para tool-calls' : 'Mostra payloads brutos'}
                        primaryTypographyProps={{ fontSize: 12 }}
                        secondaryTypographyProps={{ fontSize: 10.5, color: theme.palette.text.disabled }}
                    />
                    <Switch
                        size="small"
                        checked={friendlyMode}
                        onChange={(_, v) => setFriendlyMode(v)}
                        onClick={(e) => e.stopPropagation()}
                    />
                </MenuItem>

                <MenuItem
                    onClick={() => setShowToolCalls(!showToolCalls)}
                    sx={(theme) => ({ fontSize: 12, color: theme.palette.text.primary, py: 1 })}
                >
                    <ListItemText
                        primary="Mostrar tool-calls"
                        secondary="Inclui chamadas de ferramentas no chat (além do painel de execução)"
                        primaryTypographyProps={{ fontSize: 12 }}
                        secondaryTypographyProps={{ fontSize: 10.5, color: theme.palette.text.disabled }}
                    />
                    <Switch
                        size="small"
                        checked={showToolCalls}
                        onChange={(_, v) => setShowToolCalls(v)}
                        onClick={(e) => e.stopPropagation()}
                    />
                </MenuItem>

                {showSessionInfo !== false && [
                    <Divider key="debug-divider" sx={(theme) => ({ borderColor: theme.palette.border.soft, my: 0.5 })} />,
                    <Box
                        key="debug-label"
                        sx={(theme) => ({
                            px: 2,
                            pt: 1,
                            pb: 0.5,
                            fontSize: 10,
                            color: theme.palette.text.disabled,
                            textTransform: 'uppercase',
                            letterSpacing: 0.6,
                            fontWeight: 500,
                        })}
                    >
                        Debug
                    </Box>,
                    <MenuItem
                        key="connection-toggle"
                        onClick={() => handleToggleConnection(!isConnected)}
                        sx={(theme) => ({ fontSize: 12, color: theme.palette.text.primary, py: 1 })}
                    >
                        <ListItemText
                            primary="Conexão WebSocket"
                            secondary={isConnected ? 'Conectado — alternar simula desconexão' : 'Desconectado — alternar reabre a sessão'}
                            primaryTypographyProps={{ fontSize: 12 }}
                            secondaryTypographyProps={{ fontSize: 10.5, color: theme.palette.text.disabled }}
                        />
                        <Switch
                            size="small"
                            checked={isConnected}
                            onChange={(_, v) => handleToggleConnection(v)}
                            onClick={(e) => e.stopPropagation()}
                        />
                    </MenuItem>,
                    <MenuItem
                        key="session-data"
                        onClick={() => {
                            setMenuAnchor(null);
                            setSessionDialogOpen(true);
                        }}
                        sx={(theme) => ({ fontSize: 12, color: theme.palette.text.primary, py: 1 })}
                    >
                        <ListItemIcon sx={(theme) => ({ color: theme.palette.text.secondary, minWidth: 28 })}>
                            <DataObjectIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                            primary="Ver dados da sessão"
                            primaryTypographyProps={{ fontSize: 12 }}
                        />
                    </MenuItem>,
                ]}
            </Menu>

            <SessionDataDialog
                open={sessionDialogOpen}
                onClose={() => setSessionDialogOpen(false)}
            />

            {/* Image preview lightbox. Opens when the user clicks an
                attachment thumbnail; closes on backdrop click, ESC,
                or the explicit close button. The image scales to fit
                the viewport (max 90vw / 90vh) so portrait shots and
                wide screenshots both stay fully visible. */}
            <Modal
                open={previewAttachment !== null}
                onClose={() => setPreviewAttachment(null)}
                aria-labelledby="prompt-attachment-preview"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
                slotProps={{
                    backdrop: {
                        sx: { backgroundColor: alpha('#000', 0.85) },
                    },
                }}
            >
                <Box
                    onClick={() => setPreviewAttachment(null)}
                    sx={{
                        position: 'relative',
                        outline: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100vw',
                        height: '100vh',
                    }}
                >
                    {previewAttachment && (
                        <Box
                            component="img"
                            src={previewAttachment.dataUrl}
                            alt={previewAttachment.name}
                            // Stop click on the image itself from closing
                            // the modal so users can interact with it
                            // (right-click → save, drag, etc.).
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                                maxWidth: '90vw',
                                maxHeight: '90vh',
                                objectFit: 'contain',
                                borderRadius: 1,
                                boxShadow: 6,
                            }}
                        />
                    )}
                    <IconButton
                        onClick={() => setPreviewAttachment(null)}
                        aria-label="Fechar pré-visualização"
                        sx={(theme) => ({
                            position: 'absolute',
                            top: 16,
                            right: 16,
                            backgroundColor: alpha(theme.palette.background.default, 0.7),
                            color: theme.palette.common.white,
                            '&:hover': {
                                backgroundColor: alpha(theme.palette.background.default, 0.9),
                            },
                        })}
                    >
                        <CloseIcon />
                    </IconButton>
                    {previewAttachment && (
                        <Typography
                            id="prompt-attachment-preview"
                            sx={(theme) => ({
                                position: 'absolute',
                                bottom: 16,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 1,
                                backgroundColor: alpha(theme.palette.background.default, 0.7),
                                color: theme.palette.common.white,
                                fontSize: 12,
                                fontFamily: theme.typography.fontFamilyMono,
                                maxWidth: '80vw',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            })}
                        >
                            {previewAttachment.name}
                        </Typography>
                    )}
                </Box>
            </Modal>

            {/* Subtle hint while a turn is in flight. The cancel/pause
                affordances live in RunProgressPanelV2 so this row stays
                minimal — a single status line, no buttons. */}
            {isTurnOpen && false && (
                <Typography
                    sx={(theme) => ({
                        mt: 0.5,
                        fontSize: 10.5,
                        color: theme.palette.text.disabled,
                        fontFamily: theme.typography.fontFamilyMono,
                        textAlign: 'center',
                    })}
                >
                    O agente está respondendo. Use Pausar / Cancelar no painel à direita para interromper.
                </Typography>
            )}
        </Box>
    );
});

PromptV2.displayName = 'PromptV2';
